import React, { useEffect, useRef } from 'react';

import { motion } from 'framer-motion';
import { useNavigate , Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './styles.css';

const CapturedMomentsSection = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'ar';
  
  const row1Ref = useRef(null);
  const row2Ref = useRef(null);

  // Images pour la première ligne (1-6)
  const row1Images = [
    '/image/events/1.jpeg',
    '/image/events/2.jpg',
    '/image/events/3.jpeg',
    '/image/events/4.jpg',
    '/image/events/5.jpeg',
    '/image/events/6.jpeg'
  ];

  // Images pour la deuxième ligne (7-11)
  const row2Images = [
    '/image/events/7.jpeg',
    '/image/events/8.jpeg',
    '/image/events/9.jpeg',
    '/image/events/10.jpeg',
    '/image/events/11.jpeg'
  ];

  // Dupliquer les images pour l'effet de scroll infini
  const duplicatedRow1 = [...row1Images, ...row1Images, ...row1Images];
  const duplicatedRow2 = [...row2Images, ...row2Images, ...row2Images];

  // Auto-scroll pour la première ligne (gauche vers droite)
  useEffect(() => {
    const row1 = row1Ref.current;
    if (!row1) return;

    let scrollPosition = 0;
    const scrollSpeed = 0.5;
    let animationFrameId;

    const autoScroll = () => {
      scrollPosition += scrollSpeed;
      const maxScroll = row1Images.length * 320; // 300px width + 20px gap

      if (scrollPosition >= maxScroll) {
        scrollPosition = 0;
      }

      row1.style.transform = `translateX(-${scrollPosition}px)`;
      animationFrameId = requestAnimationFrame(autoScroll);
    };

    animationFrameId = requestAnimationFrame(autoScroll);

    return () => cancelAnimationFrame(animationFrameId);
  }, [row1Images.length]);

  // Auto-scroll pour la deuxième ligne (droite vers gauche)
  useEffect(() => {
    const row2 = row2Ref.current;
    if (!row2) return;

    let scrollPosition = 0;
    const scrollSpeed = 0.5;
    let animationFrameId;

    const autoScroll = () => {
      scrollPosition -= scrollSpeed;
      const maxScroll = -row2Images.length * 320;

      if (scrollPosition <= maxScroll) {
        scrollPosition = 0;
      }

      row2.style.transform = `translateX(${scrollPosition}px)`;
      animationFrameId = requestAnimationFrame(autoScroll);
    };

    animationFrameId = requestAnimationFrame(autoScroll);

    return () => cancelAnimationFrame(animationFrameId);
  }, [row2Images.length]);

  return (
    <section className="captured-moments-section">
      <div className="captured-moments-container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="section-title">
            {t('capturedMoments.title')}
          </h2>
          <p className="section-subtitle">
            {t('capturedMoments.subtitle')}
          </p>
        </motion.div>

        <div className="gallery-wrapper">
          {/* Première ligne - scroll de gauche à droite */}
          <div className="gallery-row">
            <div className="gallery-track" ref={row1Ref}>
              {duplicatedRow1.map((image, index) => (
                <div key={`row1-${index}`} className="gallery-item">
                  <img src={image} alt={`Moment ${index + 1}`} />
                </div>
              ))}
            </div>
          </div>

          {/* Deuxième ligne - scroll de droite à gauche */}
          <div className="gallery-row">
            <div className="gallery-track" ref={row2Ref}>
              {duplicatedRow2.map((image, index) => (
                <div key={`row2-${index}`} className="gallery-item">
                  <img src={image} alt={`Moment ${index + 7}`} />
                </div>
              ))}
            </div>
          </div>
        </div>

        <motion.div
          className="cta-button-container"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <Link
            className="cta-button"
            to="/events"
          >
            {t('capturedMoments.viewEvents')}
            <span className={`arrow ${isRTL ? 'rtl' : ''}`}>→</span>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default CapturedMomentsSection;
